import { useMemo } from 'react';
import { diffJson, jsonLines, markForPointer, type ChangeMark } from '../lib/jsonDiff';
import { strings } from '../strings';

const SYMBOL: Record<ChangeMark, string> = { added: '+', removed: '−', modified: '~' };

function Column({ title, value, marks }: { title: string; value: unknown; marks: Map<string, ChangeMark> }) {
  const lines = useMemo(() => jsonLines(value), [value]);
  return (
    <div className="min-w-0 flex-1">
      <h3 className="mb-1 text-sm font-semibold">{title}</h3>
      <div className="max-h-[60vh] overflow-auto rounded-md border border-slate-300 dark:border-slate-600">
        <pre className="min-w-max py-1 font-mono text-xs leading-5">
          {lines.map((line, i) => {
            const mark = markForPointer(marks, line.pointer);
            return (
              <div key={i} className={`flex ${mark ? `diff-${mark}` : ''}`}>
                <span aria-hidden className="w-5 shrink-0 text-center select-none">
                  {mark ? SYMBOL[mark] : ''}
                </span>
                {mark && <span className="sr-only">{strings.diff[mark]}: </span>}
                <span style={{ paddingLeft: `${line.depth * 1.25}em` }} className="pr-3">
                  {line.text}
                </span>
              </div>
            );
          })}
        </pre>
      </div>
    </div>
  );
}

/** Side-by-side JSON diff (jsondiffpatch); changed lines are marked with a symbol and a color. */
export function JsonDiffView({
  left,
  right,
  leftTitle,
  rightTitle,
}: {
  left: unknown;
  right: unknown;
  leftTitle: string;
  rightTitle: string;
}) {
  const marks = useMemo(() => diffJson(left, right), [left, right]);
  return (
    <div>
      <p className="help mb-2">{marks.changed ? strings.diff.legend : strings.versions.noDifferences}</p>
      <div className="flex flex-col gap-3 md:flex-row">
        <Column title={leftTitle} value={left} marks={marks.left} />
        <Column title={rightTitle} value={right} marks={marks.right} />
      </div>
    </div>
  );
}
